import React from "react";
import { Skeleton, Space, Tag, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  LoginOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  CHURN_EVALUATED_STATUSES,
  ELIGIBILITY_REASON_LABELS,
  formatChurnActivityPhrase,
  formatChurnLoginPhrase,
  getChurnStatusMeta,
} from "./orgChurn";

const { Text } = Typography;

export const STATUS_EXPLANATIONS = {
  ok: "Login e attività sono entro le soglie configurate.",
  login_inactive:
    "Il login supera la soglia, ma l'attività operativa è ancora entro soglia.",
  creation_inactive:
    "L'attività operativa supera la soglia, ma il login è ancora entro soglia.",
  churn_risk:
    "Login e attività operativa superano entrambe le soglie configurate.",
  disabled: "Il calcolo è disabilitato dalla configurazione churn applicata.",
  not_applicable: "L'organizzazione è esclusa dal calcolo churn.",
  config_missing: "Non è disponibile una configurazione churn valida.",
};

// Un'icona dedicata per ogni stato: insieme al colore rende lo stato leggibile
// a colpo d'occhio e distinguibile anche da chi non percepisce bene i colori.
export const STATUS_ICONS = {
  ok: CheckCircleOutlined,
  login_inactive: LoginOutlined,
  creation_inactive: EditOutlined,
  churn_risk: WarningOutlined,
  disabled: StopOutlined,
  not_applicable: MinusCircleOutlined,
  config_missing: QuestionCircleOutlined,
};

// Il tooltip antd ha sfondo scuro di default: i testi usano toni chiari.
const tooltipTitleStyle = {
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  marginBottom: 2,
};
const tooltipBodyStyle = {
  color: "rgba(255, 255, 255, 0.75)",
  fontSize: 12,
  lineHeight: "18px",
};
const tooltipLabelStyle = {
  color: "rgba(255, 255, 255, 0.65)",
  fontSize: 12,
  whiteSpace: "nowrap",
};
const formatDays = (value) => ((value ?? null) === null ? "-" : `${value} gg`);

const renderChurnTooltip = (indicator, meta) => {
  if (!indicator) {
    return (
      <div style={{ maxWidth: 260 }}>
        <div style={tooltipTitleStyle}>Indicatore non disponibile</div>
        <div style={tooltipBodyStyle}>
          La Lambda non ha ancora calcolato un indicatore churn per questa org.
        </div>
      </div>
    );
  }

  const evaluated = CHURN_EVALUATED_STATUSES.includes(indicator.status);
  const hasThresholds =
    indicator.login_inactivity_days != null ||
    indicator.creation_inactivity_days != null;

  return (
    <div style={{ maxWidth: 260 }}>
      <div style={tooltipTitleStyle}>{meta.label}</div>
      {evaluated ? (
        <div style={{ marginTop: 6 }}>
          <div style={tooltipBodyStyle}>{formatChurnLoginPhrase(indicator)}</div>
          <div style={tooltipBodyStyle}>
            {formatChurnActivityPhrase(indicator)}
          </div>
        </div>
      ) : (
        <div style={{ ...tooltipBodyStyle, marginTop: 6 }}>
          {STATUS_EXPLANATIONS[indicator.status] ||
            "Stato churn calcolato dalla Lambda."}
        </div>
      )}
      {hasThresholds && (
        <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>
          Soglie: login {formatDays(indicator.login_inactivity_days)} · attività{" "}
          {formatDays(indicator.creation_inactivity_days)}
        </div>
      )}
    </div>
  );
};

const OrgChurnStatus = ({
  indicator,
  compact = false,
  loading = false,
  hideNotApplicable = false,
}) => {
  // In caricamento, mostriamo un placeholder al posto del finto "n/d":
  // la colonna si popola con una fetch separata e senza questo lampeggerebbe
  // "n/d" su tutte le righe finché i dati non arrivano.
  if (loading && !indicator) {
    return (
      <Skeleton.Button
        active
        size="small"
        shape="round"
        style={{ width: 64, minWidth: 64, height: 22 }}
      />
    );
  }

  if (!indicator) {
    return (
      <Tooltip title={renderChurnTooltip(null)}>
        <Text type="secondary" style={{ cursor: "default" }}>
          —
        </Text>
      </Tooltip>
    );
  }

  const meta = getChurnStatusMeta(indicator.status);

  // Su superfici a lista (tabella/card) lo stato "non applicabile" è solo
  // rumore: lo riduciamo a un trattino discreto, con i dettagli al hover.
  if (hideNotApplicable && indicator.status === "not_applicable") {
    return (
      <Tooltip title={renderChurnTooltip(indicator, meta)}>
        <Text type="secondary" style={{ cursor: "default" }}>
          —
        </Text>
      </Tooltip>
    );
  }

  const StatusIcon = STATUS_ICONS[indicator.status];
  const showEligibility =
    indicator.eligibility_reason &&
    indicator.eligibility_reason !== "eligible";

  return (
    <Tooltip title={renderChurnTooltip(indicator, meta)}>
      <Space size={4} wrap>
        <Tag
          color={meta.color}
          icon={StatusIcon ? <StatusIcon /> : undefined}
          style={{ margin: 0 }}
        >
          {compact ? meta.label : meta.label.toUpperCase()}
        </Tag>
        {!compact && showEligibility && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ELIGIBILITY_REASON_LABELS[indicator.eligibility_reason] ||
              indicator.eligibility_reason}
          </Text>
        )}
      </Space>
    </Tooltip>
  );
};

export default OrgChurnStatus;
